/**
 * Windowing — which rows to render, and how much empty space stands in for the
 * rest.
 *
 * ## Why this is a function and not a hook
 *
 * The same reason `apps/app`'s `attentionStack` is a function: so it can be
 * probed without a browser. Neither `packages/ui` nor `apps/app` runs jsdom, and
 * the interesting failures here are arithmetic — a window that drops the last
 * row, padding that does not sum to the full extent, a scroll position past the
 * end of a list that a filter just shortened. Every one of those is a property
 * of four numbers, and a property of four numbers should not need a DOM to
 * state.
 *
 * The component holds `scrollTop` and calls this; a real browser's scrolling is
 * c38's Playwright work, which is the only place it can honestly be tested.
 *
 * ## The invariant that makes the scrollbar correct
 *
 * `padTop + (end - start) * rowHeight + padBottom === rowCount * rowHeight`
 *
 * The rendered rows plus the two spacers must occupy exactly the height the full
 * list would. Break it and the scrollbar describes a list of a different length
 * than the one being scrolled — the thumb drifts, and the end of the list either
 * cannot be reached or is followed by blank space. It is stated here rather than
 * left implicit because it is the one property a reader cannot see by looking at
 * the four expressions below.
 */
import { TABLE_OVERSCAN_ROWS, TABLE_ROW_HEIGHT } from "./geometry.js";
/**
 * The window for a given scroll position.
 *
 * `viewportHeight` is the height of the scrolling element, not of the document:
 * a table in a page that scrolls as a whole is not windowed at all, and the
 * component treats an absent height as "render everything" rather than guessing
 * one.
 */
export function rowWindow(scrollTop, viewportHeight, rowCount, rowHeight = TABLE_ROW_HEIGHT, overscan = TABLE_OVERSCAN_ROWS) {
    if (rowCount <= 0 || rowHeight <= 0) {
        return { start: 0, end: 0, padTop: 0, padBottom: 0 };
    }
    // Negative scroll is what an over-scroll bounce reports on macOS and iOS, and
    // a negative `start` would slice from the end of the array — the rows would be
    // the last twelve of the list, rendered at the top, which reads as corruption
    // rather than as a rounding error.
    const top = Math.max(0, scrollTop);
    const height = Math.max(0, viewportHeight);
    const firstVisible = Math.floor(top / rowHeight);
    // `ceil` plus one: a viewport 3.2 rows tall shows four rows when scrolled to a
    // row boundary and five when scrolled between two, and the window has to cover
    // the worse case or the last row is blank for one frame on every scroll.
    const visibleCount = Math.ceil(height / rowHeight) + 1;
    // Clamped to `rowCount` on both ends. The case that needs it: a filter shrinks
    // the list while the user is scrolled two hundred rows down, so `firstVisible`
    // is past the end. Without the clamp `start > end` and the slice is empty
    // while `padTop` claims a list that is no longer there.
    const start = Math.min(Math.max(0, firstVisible - overscan), rowCount);
    const end = Math.min(rowCount, firstVisible + visibleCount + overscan);
    return {
        start,
        end,
        padTop: start * rowHeight,
        padBottom: (rowCount - end) * rowHeight,
    };
}
//# sourceMappingURL=window.js.map